<template>
  <AuthLayout>
    <!-- Step 1: 비밀번호 입력 -->
    <div v-if="step === 1">
      <div class="reset-head">
        <div class="reset-badge">비밀번호 재설정</div>
        <h1 class="reset-title">Reset Password</h1>
      </div>

      <form class="reset-form" @submit.prevent="handleSubmit">
        <UiInput
          v-model="form.password"
          type="password"
          placeholder="새 비밀번호"
          autocomplete="new-password"
        />

        <UiInput
          v-model="form.passwordConfirm"
          type="password"
          placeholder="새 비밀번호 확인"
          autocomplete="new-password"
        />

        <UiButton
          type="submit"
          :disabled="!canSubmit || loading"
          :loading="loading"
        >
          비밀번호 재설정
        </UiButton>
      </form>
    </div>

    <!-- Step 2: 성공 화면 -->
    <div v-else class="success-content">
      <div class="success-icon">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="50" fill="#2A4624" />
          <circle cx="50" cy="50" r="36" fill="#78FF47" />
          <path
            d="M35 50 L45 60 L65 40"
            stroke="#000000"
            stroke-width="5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="success-title">비밀번호가 재설정되었습니다!</h2>
      <p class="success-message">새로운 비밀번호로 로그인해주세요.</p>

      <div class="success-actions">
        <UiButton @click="goToSignIn">로그인하기</UiButton>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import { api } from "@/lib/api";
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AuthLayout from "@/components/AuthLayout.vue";

export default {
  name: "ResetPasswordView",
  components: { UiButton, UiInput, AuthLayout },
  data() {
    return {
      step: 1,
      loading: false,
      token: "",
      tokenValid: false,
      form: {
        password: "",
        passwordConfirm: "",
      },
    };
  },
  computed: {
    passwordValid() {
      return this.form.password && this.form.password.length >= 6;
    },
    passwordMatch() {
      return this.form.password === this.form.passwordConfirm;
    },
    canSubmit() {
      return (
        this.form.password &&
        this.form.passwordConfirm &&
        this.passwordValid &&
        this.passwordMatch
      );
    },
  },
  mounted() {
    this.token = (this.$route.query.token || "").toString();
    if (!this.token) {
      this.$router.push("/signin");
      return;
    }
    this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        const res = await api("/v1/auth/reset-password/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: this.token }),
        });
        if (!res.ok) throw new Error("invalid");
        this.tokenValid = true;
      } catch {
        this.$router.push("/signin");
      }
    },
    async handleSubmit() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;

      try {
        await api("/v1/auth/reset-password/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: this.token,
            new_password: this.form.password,
          }),
        });

        this.step = 2;
      } catch (e) {
        // do nothing
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push("/signin");
    },
  },
};
</script>

<style scoped>
/* 제목 */
.reset-head {
  margin-bottom: 24px;
  text-align: left;
}

.reset-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.reset-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.reset-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* UiInput */
.reset-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}

.reset-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.reset-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.reset-form .ui-btn {
  width: 100%;
}

/* 성공 화면 */
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.success-icon {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.success-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -2px;
  color: #ffffff;
  text-align: center;
}

.success-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.8);
}

.success-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.success-actions .ui-btn {
  width: 100%;
}

@media (max-width: 640px) {
  .reset-title,
  .success-title {
    font-size: 32px;
  }
}
</style>
